'use client';

import { DiscountBanner } from '@/components';
import ErrorDialog from '@/components/ErrorDialog/ErrorDialog';
import Footer from '@/components/Footer/Footer';
import Header from '@/components/Header/Header';
import LogInDialog from '@/components/LogInDialog/LogInDialog';
import { datadogRumInit } from '@/core/logger/logger';
import { useSession } from '@/hooks';
import { usePrenuvoStore } from '@/stores/usePrenuvoStore';
import { GTM_ID } from '@/utils/gtm';
import { HaloProvider } from '@prenuvo/halo-web';
import { addBasePath } from 'next/dist/client/add-base-path';
import { Lato } from 'next/font/google';
import localFont from 'next/font/local';
import { useEffect } from 'react';
import { ReactTagManager } from 'react-gtm-ts';
import { Helmet } from 'react-helmet';
import { useShallow } from 'zustand/react/shallow';
import appConfig from '../../package.json';
import './globals.css';
import { PostHogProvider } from './PostHogProvider';

if (typeof window !== 'undefined') {
  datadogRumInit();
}

const sofiaPro = localFont({
  src: [
    {
      path: '../static/fonts/sofia-pro-semibold.woff',
      weight: '600',
      style: 'normal',
    },
    {
      path: '../static/fonts/sofia-pro-bold.woff',
      weight: '700',
      style: 'normal',
    },
  ],
  display: 'swap',
  variable: '--font-sofia-pro',
});

const lato = Lato({
  subsets: ['latin'],
  display: 'swap',
  variable: '--font-lato',
  weight: ['100', '300', '400', '700', '900'],
});

ReactTagManager.init({
  code: GTM_ID,
  debug: false,
  performance: true,
});

// TODO: add metadata back in phase 2 with Server Side Rendering
export default function RootLayout({
  children,
}: Readonly<{
  children: React.ReactNode;
}>) {
  const { triggeruseGetSession } = useSession();
  useEffect(() => {
    triggeruseGetSession();
  }, [triggeruseGetSession]);

  // TODO: improve organization of loading state
  const { isInsideCard, loading } = usePrenuvoStore(
    useShallow((state) => ({
      isInsideCard: state.ui.isInsideCard,
      loading: state.ui.loading.state,
    })),
  );

  return (
    <html lang="en">
      <Helmet>
        <meta
          name="description"
          content="At Prenuvo, we provide whole body, radiation-free MRI scans in order for our patients to focus on early detection of over 500 cancers and diseases."
        />
        <title>Whole Body MRI Scans | Screen for 500 Cancers & Diseases | Prenuvo</title>
        <meta name="version" content={appConfig.version} />
      </Helmet>
      <body className={`${lato.variable} ${sofiaPro.variable} app-bg relative min-h-dvh font-sans`}>
        {/* TODO: improve scrappy solution */}
        <div
          className={`fixed inset-0 flex h-full w-full items-center justify-center ${loading ? 'obv3-show' : ''} obv3-fade-in`}
        >
          <img
            src={`${addBasePath('/images/loading-logo.gif')}`}
            alt="Loading"
            className="h-[75px] w-[105px] opacity-80"
          />
        </div>

        <PostHogProvider>
          <HaloProvider language="en" locales={{ en: {} }}>
            <Header />
            <DiscountBanner />
            <a
              href="#main"
              className="skip-to-content focus:ring-gray-500 sr-only absolute left-0 top-0 m-4 -translate-y-full focus:not-sr-only focus:translate-y-0 focus:outline-none focus:ring"
            >
              Skip to main content
            </a>
            <main className="sm:px-4">
              {isInsideCard ? (
                <div
                  className={`obv3-fade-in relative mx-auto min-h-96 max-w-[800px] sm:mt-4 ${loading ? '' : 'obv3-show'}`}
                >
                  <>{children}</>
                </div>
              ) : (
                <>{children}</>
              )}
            </main>
            <Footer />
            <ErrorDialog />
            <LogInDialog />
          </HaloProvider>
        </PostHogProvider>
      </body>
    </html>
  );
}
